import '@yourcoach/shared/utils/datetime/setupDayjs';

import {
  type FC,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import 'react-contexify/ReactContexify.css';
import {createPortal} from 'react-dom';
import {Helmet} from 'react-helmet';
import {Outlet, useLocation, useNavigate} from 'react-router';
import {useTypedSearchParams} from 'react-router-typesafe-routes';
import {ToastContainer} from 'react-toastify';

import NiceModal from '@ebay/nice-modal-react';
import {
  API_VERSION,
  B2B_API_VERSION,
  IS_WIDGET_ON_MOBILE_KEY,
  TITLE,
} from '@root/config';
import {reaction, when} from 'mobx';
import styled, {createGlobalStyle} from 'styled-components';

import type {ApiRpcRequest} from '@yourcoach/shared/api/utils/apiRequest';
import type {StopConferenceConfig} from '@yourcoach/shared/modules/conference';
import type {RateConferenceConfig} from '@yourcoach/shared/modules/conference-rating';

import {i18n} from '@yourcoach/i18n';
import {authStore} from '@yourcoach/shared/api/auth';
import {categoryStore} from '@yourcoach/shared/api/category';
import {rewardProfileStore} from '@yourcoach/shared/api/gamification';
import {membershipStore} from '@yourcoach/shared/api/membership';
import {cardStore} from '@yourcoach/shared/api/payment';
import {currentUserStore} from '@yourcoach/shared/api/user';
import {apiRequest} from '@yourcoach/shared/api/utils/apiRequest';
import {alert, AlertContainer} from '@yourcoach/shared/components/alert';
import {ErrorBoundary} from '@yourcoach/shared/components/ErrorBoundary';
import {LightboxContainer} from '@yourcoach/shared/components/lightbox';
import {ModalService} from '@yourcoach/shared/components/Modal/stores/ModalService/Modal.service';
import {UserWaitlistStore} from '@yourcoach/shared/core/stores/UserWaitlist.store';
import {CachedMediaDbService} from '@yourcoach/shared/core/system/cachedMediaDB/CachedMediaDb.service';
import {PendingPostsService} from '@yourcoach/shared/core/system/pendingPosts/PendingPosts.service';
import {InjectionProvider} from '@yourcoach/shared/IoC';
import {createRootContainerApp} from '@yourcoach/shared/IoC/containerFactories/createRootContainer.app';
import {RequestPhoneNumberStore} from '@yourcoach/shared/modules/accounts';
import {LiveSessionStrategy} from '@yourcoach/shared/modules/book-conference';
import {runBookConferenceFlow} from '@yourcoach/shared/modules/book-conference/runBookConferenceFlow';
import {conferenceStopHandler} from '@yourcoach/shared/modules/conference';
import {runRateConferenceFlow} from '@yourcoach/shared/modules/conference-rating';
import {AppNewVersionService} from '@yourcoach/shared/services/app-new-version';
import {privacyPolicyUpdateService} from '@yourcoach/shared/services/privacy-policy-update';
import {syncTimeZoneService} from '@yourcoach/shared/services/sync-time-zone';
import {ThemeContext} from '@yourcoach/shared/styles/ThemeProvider';
import {getColor} from '@yourcoach/shared/styles/utils';
import {appStateManager} from '@yourcoach/shared/utils/appState';
import {coreOrB2bEnvSwitcher} from '@yourcoach/shared/utils/coreOrB2b/CoreOrB2bEnvSwitcher';
import {createFile} from '@yourcoach/shared/utils/entity/file';
import {envManager} from '@yourcoach/shared/utils/env';
import {
  emitter,
  WS_RECEIVE_MESSAGE_EVENT,
} from '@yourcoach/shared/utils/eventEmitter';
import {logger} from '@yourcoach/shared/utils/logger';
import {reloadApp} from '@yourcoach/shared/utils/reloadApp';
import {storage} from '@yourcoach/shared/utils/storage';

import {useSetReturnPath} from '@src/common/useUserMethods';
import WS from '@src/components/WS/WS';
import '@src/styles/index.css';
import {PageLoading} from '@src/v2/components/PageLoading';
import {b2cDemoStore} from '@src/v2/modules/b2c-demo';
import {DocumentViewerModal} from '@src/v2/modules/document-viewer-modal';
import {RatingConferenceModal} from '@src/v2/modules/rating-conference-modal';
import {NavigationServiceProvider} from '@src/v2/services/navigation/NavigationServiceProvider';
import {Logger} from '@src/v2/utils/logger';

import {routerPath} from './routes/path';
import {AddAccountsModal} from './v2/modules/accounts/AddAccountModal';

const GlobalStyles = createGlobalStyle`
  #nprogress {
    .bar {
      background: ${getColor('global1')}
    }

    .spinner {
      display: none;
    }
  }
`;

const init = async (isB2b = false) => {
  const apiEndpoint = isB2b
    ? process.env.B2B_API_ENDPOINT!
    : process.env.API_ENDPOINT!;

  envManager.__setEnv({
    apiEndpoint: process.env.API_ENDPOINT!,
    apiVersion: API_VERSION,
    // @ts-expect-error: string vs union
    appEnv: process.env.APP_ENV,
    b2bApiEndpoint: process.env.B2B_API_ENDPOINT!,
    b2bApiVersion: B2B_API_VERSION,
    communityProgramId: process.env.COMMUNITY_PROGRAM_ID!,
    googleMapsKey: process.env.GOOGLE_MAPS_KEY!,
    isB2b,
    stripeKey: process.env.STRIPE_KEY!,
    vonageAppId: isB2b
      ? process.env.B2B_VONAGE_APP_ID!
      : process.env.VONAGE_APP_ID!,
  });

  apiRequest.configure({
    apiEndpoint,
    apiVersion: isB2b ? B2B_API_VERSION : API_VERSION,
    onRequestDidEnd: (payload: ApiRpcRequest | ApiRpcRequest[]) => {
      logger.logApiRequest(payload);
    },
  });

  logger.__setPlatformLogger(new Logger());

  if (
    // see mobile_app/app/screens/Widget.tsx
    typeof __MOBILE_WIDGET_INIT_DATA !== 'undefined' &&
    (await storage.getItem(IS_WIDGET_ON_MOBILE_KEY)) !== 'true'
  ) {
    const {session, session_key, user, user_key} = __MOBILE_WIDGET_INIT_DATA;

    // TODO: Should we expose the env switcher key outside?
    storage.setItem('cb2b_env', 'b2b');
    storage.setItem(session_key, session);
    storage.setItem(user_key, user);
    storage.setItem(IS_WIDGET_ON_MOBILE_KEY, 'true');

    reloadApp();
  }
};

const App: FC = () => {
  coreOrB2bEnvSwitcher.setup(process.env.APP_ENV!);

  const rootContainer = useMemo(() => createRootContainerApp(), []);

  const {setAccentColor, theme, themes} = useContext(ThemeContext);

  const setRetPath = useSetReturnPath();

  const navigate = useNavigate();
  const location = useLocation();
  const [{co: coPartner, color: demoColor, demo}, setSearchParams] =
    useTypedSearchParams(routerPath);

  const [i18nIsReady, setI18nIsReady] = useState(__SSR__);
  const [isEnvReady, setIsEnvReady] = useState(__SSR__);
  const [isCachedMediaDbReady, setIsCachedMediaDbReady] = useState(false);

  // demo mode
  const demoPartnerId = demo;

  if (demoPartnerId && process.env.APP_ENV !== 'prod') {
    b2cDemoStore.init(demoPartnerId);
    b2cDemoStore.setColor(demoColor ?? null);
    b2cDemoStore.setIsCoPartner(coPartner === '1');
  }

  useEffect(
    () =>
      when(
        () => coreOrB2bEnvSwitcher.isReady,
        () => {
          init(coreOrB2bEnvSwitcher.isB2b).then(() => {
            if (demoPartnerId) {
              if (b2cDemoStore.isActive.isTrue) {
                setAccentColor(
                  {
                    dark: b2cDemoStore.color!,
                    light: b2cDemoStore.color!,
                  },
                  true,
                );
              }
            } else if (coreOrB2bEnvSwitcher.isB2b) {
              setAccentColor(
                {
                  dark: themes.dark.color.v3.static.b2b,
                  light: themes.light.color.v3.static.b2b,
                },
                true,
              );
            } else {
              setAccentColor({
                dark: themes.dark.color.v3.background.primary,
                light: themes.light.color.v3.background.primary,
              });
            }

            setIsEnvReady(true);
          });
        },
      ),
    [demoPartnerId, setAccentColor, themes],
  );

  useEffect(() => {
    setRetPath();

    i18n
      .init({
        namespaces: ['web-app', 'shared', 'notifications', 'onboarding'],
      })
      .finally(() => {
        setI18nIsReady(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestPhoneNumberStore = useMemo(() => {
    return rootContainer.get(RequestPhoneNumberStore);
  }, [rootContainer]);

  useEffect(() => {
    // just initialize the store
    rootContainer.get(UserWaitlistStore);
  }, [rootContainer]);

  useEffect(() => {
    rootContainer.get(PendingPostsService).uploadPendingPosts();
  }, [rootContainer]);

  useEffect(() => {
    requestPhoneNumberStore.init({
      onAddPhoneNumberClick: () => {
        NiceModal.show(AddAccountsModal, {
          type: 'phone',
        });
      },
    });
  }, [requestPhoneNumberStore]);

  useEffect(() => {
    new AppNewVersionService(process.env.APP_VERSION as string, {
      versionUrl: `${process.env.APP_URL}/api/version`,
    });
  }, []);

  useEffect(() => {
    const selector = '#root';

    const setupModal = () => {
      rootContainer.get(ModalService).init({
        ariaHideSelector: selector,
        mountElementSelector: selector,
      });
    };

    const initLocalDb = () => {
      rootContainer
        .get(CachedMediaDbService)
        .init()
        .then(() => {
          setIsCachedMediaDbReady(true);
        });
    };

    initLocalDb();

    setupModal();
  }, [rootContainer]);

  const runChecks = useCallback(async () => {
    if (!(await currentUserStore.userExists())) {
      return;
    }

    await privacyPolicyUpdateService({
      openDocument: async ({showAlert, title, url}) => {
        alert.hide();

        await NiceModal.show(DocumentViewerModal, {
          document: createFile({
            categories: ['document'],
            id: title,
            name: title,
            size: 1 * 1024,
            url,
          }),
          title,
        });

        showAlert();
      },
    });

    await syncTimeZoneService({
      onEditScheduleClick: () => {
        const pathname = routerPath.availability.$buildPath({});

        if (location.pathname === pathname) {
          // @ts-expect-error: it's a valid search param for availability page
          setSearchParams({os: '1'});
        } else {
          navigate(
            routerPath.availability.$buildPath({
              searchParams: {
                os: '1',
              },
            }),
          );
        }
      },
    });
  }, [location.pathname, setSearchParams, navigate]);

  const onAppBecameActive = useCallback(async () => {
    if (currentUserStore.user) {
      currentUserStore.fetch();
      rewardProfileStore.refresh();
      cardStore.fetchCards();
      membershipStore.fetchMemberships();
    }

    categoryStore.fetchCategories();

    runChecks();
  }, [runChecks]);

  useEffect(
    () =>
      appStateManager.onStateChange(state => {
        if (state === 'active') {
          onAppBecameActive();
        }
      }),
    [onAppBecameActive],
  );

  useEffect(() => {
    const disposers = [
      reaction(
        () => currentUserStore.user && currentUserStore.user._id,
        () => {
          logger.setUser(currentUserStore.user);

          if (currentUserStore.user) {
            cardStore.fetchCards();
            membershipStore.fetchMemberships();

            runChecks();
          }
        },
      ),
      reaction(
        () => currentUserStore.user?.subtenant?.partner_id,
        partnerId => {
          if (partnerId) {
            logger.setUserProperty('partner_id', partnerId);
          }
        },
      ),
      reaction(
        () => currentUserStore.user && currentUserStore.user.is_blocked,
        async () => {
          if (currentUserStore.user && currentUserStore.user.is_blocked) {
            /**
             * This will never be called. The backend will delete all sessions after a user is blocked.
             * So the user will be logged out because of the expired session error.
             */
            alert.clearQueue(true);

            currentUserStore.clear();
            authStore.logout().catch(() => {});
            authStore._deleteSession();

            await alert.show({
              title: i18n.t('shared:blocked_account'),
            });

            if (envManager.isB2b) {
              coreOrB2bEnvSwitcher.toggleEnv();
            }
          }
        },
      ),
    ];

    categoryStore.fetchCategories();

    return () => {
      disposers.forEach(dispose => dispose());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWsMessage = useCallback(message => {
    if (
      [
        'coach_blocked',
        'coach_unblocked',
        'points_added',
        'tier_downgraded',
        'tier_upgraded',
      ].includes(message.event.type)
    ) {
      rewardProfileStore.refresh();
    }

    if (
      [
        'coach_became_restricted',
        'coach_verified',
        'google_oauth_access_became_invalid',
      ].includes(message.event.type)
    ) {
      currentUserStore.fetch();
    }
  }, []);

  useEffect(() => {
    emitter.on(WS_RECEIVE_MESSAGE_EVENT, handleWsMessage);

    return () => {
      emitter.off(WS_RECEIVE_MESSAGE_EVENT, handleWsMessage);
    };
  }, [handleWsMessage]);

  const showRateConferenceModal = useCallback(
    (config: RateConferenceConfig) => {
      NiceModal.show(RatingConferenceModal, {
        id: config.id,
        isB2b: config.isB2b,
        role: config.role,
        type: config.type,
      });
    },
    [],
  );

  const onRateConference = useCallback(
    async (config: StopConferenceConfig) => {
      runRateConferenceFlow(config, {
        runCommonFlow: showRateConferenceModal,
      });
    },
    [showRateConferenceModal],
  );

  const onBookNextConference = useCallback(
    async ({courseId}: StopConferenceConfig) => {
      const strategy = new LiveSessionStrategy(courseId);

      runBookConferenceFlow({
        conferenceDuration: strategy.conferenceDuration,
        courseId,
        onBook: () => {
          requestPhoneNumberStore.requestPhoneNumberIfNeeded({
            place: 'ls_scheduled',
          });
        },
        place: 'book_next_popup',
        strategy,
      });
    },
    [requestPhoneNumberStore],
  );

  useEffect(() => {
    conferenceStopHandler.setRateConferenceHandler(onRateConference);
    conferenceStopHandler.setBookNextConferenceHandler(onBookNextConference);
  }, [onRateConference, onBookNextConference]);

  return (
    <>
      <Helmet defaultTitle={TITLE} titleTemplate={`%s | ${TITLE}`}>
        <meta content="width=1220" name="viewport" />
      </Helmet>
      <ErrorBoundary>
        <InjectionProvider container={rootContainer}>
          <NavigationServiceProvider>
            <NiceModal.Provider>
              <div id="allContent">
                {i18nIsReady && isEnvReady && isCachedMediaDbReady ? (
                  <Outlet />
                ) : (
                  <PageLoading />
                )}
              </div>
              <WS />
              <GlobalStyles theme={theme} />
              <StyledToastContainer
                autoClose={10000}
                hideProgressBar
                position="top-right"
              />
              <LightboxContainer />
              <Alert />
            </NiceModal.Provider>
          </NavigationServiceProvider>
        </InjectionProvider>
      </ErrorBoundary>
    </>
  );
};

const Alert = () => {
  return createPortal(<AlertContainer />, document.body);
};

const StyledToastContainer = styled(ToastContainer)`
  &&&.Toastify__toast-container--top-right {
    right: 0;
    top: 32px;
  }

  .Toastify__toast {
    width: 440px;
    box-shadow:
      0 0 15px rgba(66, 56, 181, 0.07),
      0 4px 10px rgba(53, 197, 163, 0.1);
    border-radius: 25px 0 0 25px;
    padding: 20px;
    display: flex;
    align-items: center;
  }

  .Toastify__close-button svg {
    height: 20px;
  }
`;

export default memo(App);
